/**
 * Darstellung: hell, dunkel oder wie das System, dazu ein Hintergrund.
 *
 * Die Wahl liegt im Konto und gilt damit auf allen Geräten einer Person.
 * Jedes Gerät hält eine Kopie im lokalen Speicher: Ein kleines Skript in
 * index.html setzt sie schon vor dem ersten Zeichnen (es liest denselben
 * Schlüssel), und ohne Netz bleibt die Wahl erhalten. Wer ein Gerät bewusst
 * anders einstellen möchte (am Handy dunkel, am Rechner hell), schaltet
 * „nur dieses Gerät“ ein; dann bleibt das Konto unberührt.
 */
import { DEFAULT_APPEARANCE, parseAppearance } from '@cookbook/domain';
import type { Appearance, BackgroundChoice, ThemeChoice } from '@cookbook/domain';
import { useEffect, useState } from 'react';
import { create } from 'zustand';
import type { CurrentUser } from '../api/types.ts';
import { texts } from '../texts/de.ts';
import { useAuth } from './auth.ts';
import { pendingFor, sendOrQueue } from './outbox.ts';

export { BACKGROUND_CHOICES, THEME_CHOICES } from '@cookbook/domain';
export type { BackgroundChoice, ThemeChoice } from '@cookbook/domain';

/** Schlüssel im lokalen Speicher (auch in index.html). */
export const APPEARANCE_KEY = 'kochbuch.darstellung';

/** Endpunkt für die Einstellungen im Konto. */
const PREFERENCES_PATH = '/api/auth/preferences';

/** Farbe der Statusleiste je Modus, wie in index.html. */
const THEME_COLORS = { light: '#c2410c', dark: '#1c1917' } as const;

/** Wahl im Gerät. */
interface DeviceAppearance extends Appearance {
  /** Dieses Gerät weicht bewusst vom Konto ab. */
  readonly deviceOnly: boolean;
}

/** Liest die gespeicherte Wahl; Unbekanntes fällt auf die Vorgabe zurück. */
function readDevice(): DeviceAppearance {
  try {
    const raw: unknown = JSON.parse(window.localStorage.getItem(APPEARANCE_KEY) ?? '{}');
    const record = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
    return { ...(parseAppearance(record) ?? DEFAULT_APPEARANCE), deviceOnly: record.deviceOnly === true };
  } catch {
    return { ...DEFAULT_APPEARANCE, deviceOnly: false };
  }
}

/** Setzt die Attribute am <html>-Element und die Farbe der Statusleiste. */
function applyAppearance(appearance: Appearance): void {
  const root = document.documentElement;
  // Übergänge kurz abschalten, sonst blenden Knöpfe einzeln vom alten Farbschema über.
  root.classList.add('appearance-switching');
  window.requestAnimationFrame(() =>
    window.requestAnimationFrame(() => root.classList.remove('appearance-switching')),
  );
  if (appearance.theme === 'system') {
    delete root.dataset.theme;
  } else {
    root.dataset.theme = appearance.theme;
  }
  if (appearance.background === 'plain') {
    delete root.dataset.background;
  } else {
    root.dataset.background = appearance.background;
  }
  // Die Meta-Angaben gelten je Systemmodus; bei fester Wahl bekommen beide die gewählte Farbe.
  for (const meta of document.querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')) {
    meta.dataset.original ??= meta.content;
    meta.content =
      appearance.theme === 'system'
        ? (meta.dataset.original ?? meta.content)
        : THEME_COLORS[appearance.theme];
  }
}

/** Speichert die Wahl im Gerät und wendet sie an. */
function storeDevice(device: DeviceAppearance): DeviceAppearance {
  try {
    window.localStorage.setItem(APPEARANCE_KEY, JSON.stringify(device));
  } catch {
    // Ohne Speicher gilt die Wahl nur bis zum Neuladen.
  }
  applyAppearance(device);
  return device;
}

/** Gleiche Wahl? */
function sameAppearance(a: Appearance, b: Appearance): boolean {
  return a.theme === b.theme && a.background === b.background;
}

/** Überträgt die Wahl ins Konto; ohne Netz wartet sie in der Warteschlange. */
async function saveToAccount(appearance: Appearance): Promise<void> {
  if (useAuth.getState().user === null) {
    return;
  }
  try {
    const result = await sendOrQueue<{ user: CurrentUser | null }>({
      key: 'preferences:appearance',
      method: 'PUT',
      path: PREFERENCES_PATH,
      body: { appearance: { theme: appearance.theme, background: appearance.background } },
      label: texts.sync.saveAppearance,
    });
    if (!result.queued && result.data.user !== null) {
      useAuth.setState({ user: result.data.user });
    }
  } catch {
    // Die Wahl gilt im Gerät trotzdem; beim nächsten Ändern wird es erneut versucht.
  }
}

interface AppearanceState extends DeviceAppearance {
  setTheme: (theme: ThemeChoice) => void;
  setBackground: (background: BackgroundChoice) => void;
  setDeviceOnly: (deviceOnly: boolean) => void;
}

/** Zustand der Darstellung. */
export const useAppearance = create<AppearanceState>((set, get) => {
  /** Ändert die Wahl im Gerät und, wenn das Gerät dem Konto folgt, auch dort. */
  const change = (appearance: Appearance) => {
    const next = storeDevice({ ...appearance, deviceOnly: get().deviceOnly });
    set(next);
    if (!next.deviceOnly) {
      void saveToAccount(next);
    }
  };
  return {
    ...readDevice(),
    setTheme: (theme) => change({ theme, background: get().background }),
    setBackground: (background) => change({ theme: get().theme, background }),
    setDeviceOnly: (deviceOnly) => {
      const current = get();
      set(storeDevice({ theme: current.theme, background: current.background, deviceOnly }));
      if (!deviceOnly) {
        followAccount(useAuth.getState().user);
      }
    },
  };
});

/**
 * Gleicht das Gerät mit dem Konto ab: Liegt im Konto eine Wahl, gilt sie.
 * Ist das Konto noch leer, übernimmt es die Wahl dieses Geräts.
 */
function followAccount(user: CurrentUser | null): void {
  const device = useAppearance.getState();
  // Wartet noch eine eigene Änderung, ist sie neuer als der Stand im Konto.
  if (user === null || device.deviceOnly || pendingFor(PREFERENCES_PATH).length > 0) {
    return;
  }
  const account = user.preferences?.appearance ?? null;
  if (account === null) {
    if (!sameAppearance(device, DEFAULT_APPEARANCE)) {
      void saveToAccount(device);
    }
    return;
  }
  if (!sameAppearance(device, account)) {
    useAppearance.setState(storeDevice({ ...account, deviceOnly: false }));
  }
}

/** Wendet die gespeicherte Wahl an und folgt danach dem Konto der angemeldeten Person. */
export function startAppearance(): void {
  applyAppearance(useAppearance.getState());
  followAccount(useAuth.getState().user);
  useAuth.subscribe((state, previous) => {
    if (state.user !== previous.user) {
      followAccount(state.user);
    }
  });
}

/** Ist gerade der Dunkelmodus aktiv – fest gewählt oder vom System? */
export function useIsDark(): boolean {
  const theme = useAppearance((state) => state.theme);
  const [systemDark, setSystemDark] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-color-scheme: dark)').matches,
  );
  useEffect(() => {
    const list = window.matchMedia('(prefers-color-scheme: dark)');
    const update = () => setSystemDark(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, []);
  return theme === 'dark' || (theme === 'system' && systemDark);
}
